import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useAuthStore } from './hooks/useAuth'

import ErrorBoundary from './components/ErrorBoundary'
import Layout    from './components/layout/Layout'
import Landing   from './pages/Landing'
import Login     from './pages/Login'
import Signup    from './pages/Signup'
import Dashboard from './pages/Dashboard'
import Analyze   from './pages/Analyze'
import DCA       from './pages/DCA'
import Trading   from './pages/Trading'
import Positions from './pages/Positions'
import Briefing  from './pages/Briefing'
import Options        from './pages/Options'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword  from './pages/ResetPassword'
import Settings   from './pages/Settings'
import TradeLog   from './pages/TradeLog'
import Watchlist  from './pages/Watchlist'
import Scanner    from './pages/Scanner'
import Disclaimer from './pages/Disclaimer'
import Privacy    from './pages/Privacy'
import Changelog from './pages/Changelog'
import Terms        from './pages/Terms'
import Intelligence from './pages/Intelligence'
import DJT          from './pages/DJT'

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } }
})

function Protected({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  const { token, refresh } = useAuthStore()
  useEffect(() => { if (token) refresh() }, [])

  return (
    <QueryClientProvider client={queryClient}>
     <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Private single system — no public landing or pricing */}
          <Route path="/"        element={<Navigate to="/dashboard" replace />} />
          <Route path="/login"   element={<Login />} />
          <Route path="/signup"  element={<Navigate to="/login" replace />} />
          <Route path="/pricing"    element={<Navigate to="/dashboard" replace />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy"     element={<Privacy />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/terms"       element={<Terms />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />

          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analyze"   element={<Analyze />} />
            <Route path="/trading"   element={<Trading />} />
            <Route path="/positions" element={<Positions />} />
            <Route path="/briefing"  element={<Briefing />} />
            <Route path="/options"   element={<Options />} />
            <Route path="/trade-log" element={<TradeLog />} />
            <Route path="/journal"  element={<Navigate to="/trade-log" replace />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/scanner"       element={<Scanner />} />
            <Route path="/intelligence"  element={<Intelligence />} />
            <Route path="/djt"           element={<DJT />} />
            <Route path="/dca"       element={<DCA />} />
            <Route path="/settings"  element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
     </ErrorBoundary>
    </QueryClientProvider>
  )
}
